export interface ProfileStats {
  mathDone: number;
  wordsRead: number;
  wordsWritten: number;
}

export interface Profile {
  id: string;
  name: string;
  age: number;
  grade: string;
  highScore: number;
  gamesPlayed: number;
  totalLinesCleared: number;
  stats: ProfileStats;
}

const PROFILES_KEY = 'edublox_profiles';
const ACTIVE_KEY = 'edublox_active_profile';
const MAX_PROFILES = 8;

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 8);
}

function loadProfiles(): Profile[] {
  try {
    const stored = localStorage.getItem(PROFILES_KEY);
    if (stored !== null) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {
    // localStorage unavailable
  }
  return [];
}

function saveProfiles(profiles: Profile[]): void {
  try {
    localStorage.setItem(PROFILES_KEY, JSON.stringify(profiles));
  } catch {
    // localStorage unavailable
  }
}

export function createProfile(name: string, age: number, grade: string): Profile {
  const profiles = loadProfiles();

  if (profiles.length >= MAX_PROFILES) {
    throw new Error(`No se pueden crear más de ${MAX_PROFILES} perfiles`);
  }

  const profile: Profile = {
    id: generateId(),
    name: name.trim(),
    age,
    grade,
    highScore: 0,
    gamesPlayed: 0,
    totalLinesCleared: 0,
    stats: {
      mathDone: 0,
      wordsRead: 0,
      wordsWritten: 0,
    },
  };

  profiles.push(profile);
  saveProfiles(profiles);
  return profile;
}

export function deleteProfile(id: string): boolean {
  const profiles = loadProfiles();
  const index = profiles.findIndex((p) => p.id === id);
  if (index === -1) return false;

  profiles.splice(index, 1);
  saveProfiles(profiles);

  // Clear active profile if it was deleted
  try {
    const activeId = localStorage.getItem(ACTIVE_KEY);
    if (activeId === id) {
      localStorage.removeItem(ACTIVE_KEY);
    }
  } catch {
    // localStorage unavailable
  }

  return true;
}

export function getProfile(id: string): Profile | null {
  const profiles = loadProfiles();
  return profiles.find((p) => p.id === id) ?? null;
}

export function updateProfile(id: string, data: Partial<Profile>): Profile | null {
  const profiles = loadProfiles();
  const index = profiles.findIndex((p) => p.id === id);
  if (index === -1) return null;

  // Don't allow id override
  const { id: _ignored, ...safeData } = data;
  profiles[index] = { ...profiles[index], ...safeData };
  saveProfiles(profiles);
  return profiles[index];
}

export function getAllProfiles(): Profile[] {
  return loadProfiles();
}

export function setActiveProfile(id: string): boolean {
  const profile = getProfile(id);
  if (profile === null) return false;

  try {
    localStorage.setItem(ACTIVE_KEY, id);
  } catch {
    return false;
  }
  return true;
}

export function getActiveProfile(): Profile | null {
  try {
    const activeId = localStorage.getItem(ACTIVE_KEY);
    if (activeId === null) return null;
    return getProfile(activeId);
  } catch {
    return null;
  }
}

export function clearActiveProfile(): void {
  try {
    localStorage.removeItem(ACTIVE_KEY);
  } catch {
    // localStorage unavailable
  }
}

export function addGameResult(
  profileId: string,
  score: number,
  linesCleared: number,
  challengeResults?: { mathDone: number },
): Profile | null {
  const profile = getProfile(profileId);
  if (profile === null) return null;

  const update: Partial<Profile> = {
    gamesPlayed: profile.gamesPlayed + 1,
    totalLinesCleared: profile.totalLinesCleared + linesCleared,
  };

  if (score > profile.highScore) {
    update.highScore = score;
  }

  if (challengeResults) {
    update.stats = {
      ...profile.stats,
      mathDone: profile.stats.mathDone + (challengeResults.mathDone ?? 0),
    };
  }

  return updateProfile(profileId, update);
}